import { Card } from '@optools/components/ui/card';
import { Label } from '@optools/components/ui/label';
import { Switch } from '@optools/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { cn } from '@optools/components/ui/utils';
import { CdnStatus } from '@optools/services/operator-gateway/types/generated/graphql';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { useState } from 'react';

export interface CdnCardProps {
  canDisableCdns: boolean;
  cdnStatus: CdnStatus;
  loading: boolean;
  onClick: (item: CdnStatus) => void;
  readOnly: boolean;
}

export function CdnCard({ canDisableCdns, cdnStatus, loading, onClick, readOnly }: CdnCardProps) {
  const { cdn, enabled } = cdnStatus;
  const [open, setOpen] = useState(false);
  const { cdnMapping } = useAppSelector(selectToolsConfig).btcTools.pages.cdnStatus;

  const cdnFriendlyName = cdnMapping[cdn] ?? cdn;

  const canClick = !((enabled && !canDisableCdns) || loading || readOnly);

  const handleOpenChange = (nextOpen: boolean) => {
    if ((!canDisableCdns && enabled) || readOnly) setOpen(nextOpen);
  };

  const tooltipMessage = readOnly
    ? "You don't have permission to update the CDN status"
    : `${cdnFriendlyName} cannot be disabled, there must always be one active CDN at any given time`;

  // Created this wrapper handler so that we can test that disabling the card
  // will not trigger onClick handler.
  const handleOnClick = () => {
    if (canClick) {
      onClick(cdnStatus);
    }
  };

  return (
    <Tooltip open={open} onOpenChange={handleOpenChange}>
      <TooltipTrigger asChild>
        <Card
          key={cdn}
          className={cn(
            'flex h-60 w-60 cursor-pointer flex-col items-center justify-center space-y-4 p-2',
            {
              'click:pointer-events-none opacity-50': !canClick,
              'cursor-not-allowed': readOnly,
            },
          )}
          data-testid={`${cdnStatus.cdn}-card`}
          onClick={handleOnClick}>
          <Label className="cursor-pointer text-lg">{cdnFriendlyName}</Label>
          <Switch checked={enabled} />
        </Card>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={10}>
        {tooltipMessage}
      </TooltipContent>
    </Tooltip>
  );
}
